/**
 * TicketGraoImpressao.jsx
 * Professional grain warehouse entry/exit ticket printing.
 * Opens a print window with a complete, well-formatted ticket.
 */

const produtoLabels = {
  soja: "SOJA",
  milho: "MILHO",
  sorgo: "SORGO",
  trigo: "TRIGO",
  feijao: "FEIJÃO",
  arroz: "ARROZ",
  cafe: "CAFÉ",
  algodao: "ALGODÃO",
  amendoim: "AMENDOIM",
  girassol: "GIRASSOL",
  outro: "OUTRO",
};

const classificacaoLabels = {
  tipo_1: "Tipo 1 (Padrão)",
  tipo_2: "Tipo 2",
  comercial: "Comercial",
};

function fmt(value, decimals = 0) {
  const num = parseFloat(value) || 0;
  return num.toLocaleString("pt-BR", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  });
}

function fmtPct(value) {
  const num = parseFloat(value) || 0;
  return num.toLocaleString("pt-BR", {
    minimumFractionDigits: 3,
    maximumFractionDigits: 3,
  });
}

function fmtCurrency(value) {
  const num = parseFloat(value) || 0;
  return num.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function fmtDate(dateStr) {
  if (!dateStr) return "---";
  const d = new Date(dateStr + "T12:00:00");
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString("pt-BR");
}

function padNum(num, length = 6) {
  return String(num).padStart(length, "0");
}

/**
 * @param {Object} data
 * @param {string} data.tipo - 'entrada' | 'saida' | 'transferencia'
 * @param {string} data.produto
 * @param {string} data.data
 * @param {string} data.origem_destino
 * @param {string} data.placa_veiculo
 * @param {string} data.motorista
 * @param {string} data.nota_fiscal
 * @param {string} data.classificacao
 * @param {number} data.peso_balanca_entrada
 * @param {number} data.peso_balanca_saida
 * @param {number} data.peso_bruto - net weight before discounts (entrance - tare)
 * @param {number} data.peso_liquido - final net weight after discounts
 * @param {number} data.umidade
 * @param {number} data.impureza
 * @param {number} data.avariados
 * @param {number} data.quebrados
 * @param {number} data.ardidos
 * @param {number} data.esverdeados
 * @param {number} data.desconto_umidade
 * @param {number} data.desconto_impureza
 * @param {number} data.desconto_avariados
 * @param {number} data.desconto_quebrados
 * @param {number} data.desconto_ardidos
 * @param {number} data.desconto_esverdeados
 * @param {number} data.preco_unitario - price per KG
 * @param {number} data.valor_total
 * @param {string} data.observacoes
 * @param {Object} data.propriedade - { nome, localizacao }
 * @param {Object} data.armazem - { nome }
 * @param {string} data.safra_nome
 * @param {string} data.talhao_nome
 * @param {number} data.numero_ticket - sequential number
 */
export function imprimirTicketGrao(data) {
  const tipoLabel =
    data.tipo === "entrada"
      ? "ENTRADA"
      : data.tipo === "saida"
        ? "SAÍDA"
        : "TRANSFERÊNCIA";
  const tipoColor =
    data.tipo === "entrada"
      ? "#1B6B3A"
      : data.tipo === "saida"
        ? "#B45309"
        : "#1E40AF";

  const pesoBruto = Math.max(
    parseFloat(data.peso_balanca_entrada) || 0,
    parseFloat(data.peso_balanca_saida) || 0
  );
  const pesoTara = Math.min(
    parseFloat(data.peso_balanca_entrada) || 0,
    parseFloat(data.peso_balanca_saida) || 0
  );
  const pesoVerde = pesoBruto - pesoTara;
  const pesoLiquido = parseFloat(data.peso_liquido) || 0;
  const sacas = pesoLiquido / 60;
  const precoSaca = (parseFloat(data.preco_unitario) || 0) * 60;
  const valorTotal = parseFloat(data.valor_total) || 0;
  const produtoLabel = produtoLabels[data.produto] || (data.produto || "").toUpperCase();
  const classLabel = classificacaoLabels[data.classificacao] || data.classificacao || "---";
  const fazendaNome = data.propriedade?.nome || "---";
  const fazendaLocal = data.propriedade?.localizacao || "";
  const armazemNome = data.armazem?.nome || "---";
  const ticketNum = data.numero_ticket ? padNum(data.numero_ticket) : "------";
  const now = new Date();

  const descontoUmidade = parseFloat(data.desconto_umidade) || 0;
  const descontoImpureza = parseFloat(data.desconto_impureza) || 0;
  const descontoAvariados = parseFloat(data.desconto_avariados) || 0;
  const descontoQuebrados = parseFloat(data.desconto_quebrados) || 0;
  const descontoArdidos = parseFloat(data.desconto_ardidos) || 0;
  const descontoEsverdeados = parseFloat(data.desconto_esverdeados) || 0;

  const umidade = parseFloat(data.umidade) || 0;
  const impureza = parseFloat(data.impureza) || 0;
  const avariados = parseFloat(data.avariados) || 0;
  const quebrados = parseFloat(data.quebrados) || 0;
  const ardidos = parseFloat(data.ardidos) || 0;
  const esverdeados = parseFloat(data.esverdeados) || 0;

  const html = `<!DOCTYPE html>
<html lang="pt-BR">
<head>
  <meta charset="UTF-8">
  <title>Ticket ${tipoLabel} - ${ticketNum}</title>
  <style>
    @page {
      size: A4;
      margin: 8mm;
    }
    * { margin: 0; padding: 0; box-sizing: border-box; }
    body {
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      color: #1a1a1a;
      background: #fff;
      padding: 0;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
      font-size: 11px;
    }
    .ticket {
      max-width: 100%;
      margin: 0 auto;
      border: 1.5px solid #1a1a1a;
      padding: 0;
      background: #fff;
    }

    /* ── HEADER ── */
    .header {
      background: #1B4332;
      color: white;
      padding: 10px 16px;
      display: flex;
      justify-content: space-between;
      align-items: center;
    }
    .header-left h1 {
      font-size: 14px;
      font-weight: 800;
      letter-spacing: 0.5px;
      text-transform: uppercase;
      margin-bottom: 0px;
    }
    .header-left .subtitle {
      font-size: 9px;
      opacity: 0.85;
    }
    .header-right .logo-text {
      font-size: 8px;
      opacity: 0.7;
      letter-spacing: 1.5px;
      text-transform: uppercase;
    }

    /* ── TYPE BADGE ── */
    .type-bar {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: 6px 16px;
      background: ${tipoColor};
      color: white;
      font-weight: 700;
      font-size: 13px;
      letter-spacing: 1px;
    }
    .type-bar .ticket-num {
      font-family: 'Courier New', monospace;
      font-size: 12px;
    }
    .type-bar .situacao {
      font-size: 9px;
      font-weight: 400;
      background: rgba(255,255,255,0.2);
      padding: 1px 6px;
      border-radius: 2px;
    }

    /* ── SECTIONS ── */
    .section {
      padding: 8px 16px;
      border-bottom: 1px solid #e5e5e5;
    }
    .section:last-child { border-bottom: none; }
    .section-title {
      font-size: 8px;
      font-weight: 800;
      text-transform: uppercase;
      letter-spacing: 1px;
      color: #1B4332;
      margin-bottom: 4px;
      padding-bottom: 2px;
      border-bottom: 1px solid #1B4332;
      display: inline-block;
    }

    /* ── DATA GRID ── */
    .data-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 2px 16px;
    }
    .data-row {
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      padding: 1px 0;
    }
    .data-label {
      font-size: 8px;
      color: #737373;
      text-transform: uppercase;
      font-weight: 600;
    }
    .data-value {
      font-size: 10px;
      font-weight: 700;
      color: #1a1a1a;
      text-align: right;
    }
    .data-value.mono {
      font-family: 'Courier New', monospace;
      font-size: 11px;
    }
    .data-row.full-width {
      grid-column: 1 / -1;
    }

    /* ── TABLES ── */
    .weight-table, .class-table {
      width: 100%;
      border-collapse: collapse;
      margin-top: 2px;
    }
    .weight-table th, .class-table th {
      font-size: 7px;
      text-transform: uppercase;
      color: #737373;
      font-weight: 700;
      text-align: left;
      padding: 2px 4px;
      border-bottom: 1px solid #d4d4d4;
    }
    .weight-table td, .class-table td {
      font-family: 'Courier New', monospace;
      font-size: 11px;
      font-weight: 700;
      padding: 2px 4px;
      border-bottom: 1px solid #f0f0f0;
    }
    .class-table th { background: #404040; color: #fff; text-align: center; }
    .class-table th:first-child { text-align: left; }
    .class-table td:first-child { 
      font-family: inherit; font-size: 9px; font-weight: 600; text-align: left; 
    }
    .class-table td { text-align: center; }
    .class-table .discount { color: #DC2626; }
    .right { text-align: right !important; }

    /* ── NET WEIGHT ── */
    .net-weight-box {
      background: #f9f9f9;
      border: 1px solid #1B4332;
      padding: 6px 16px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin: 4px 16px;
    }
    .net-weight-box .label {
      font-size: 10px;
      font-weight: 800;
      text-transform: uppercase;
      color: #1B4332;
    }
    .net-weight-box .value {
      font-family: 'Courier New', monospace;
      font-size: 20px;
      font-weight: 900;
      color: #1B4332;
    }
    .sacas-line {
      text-align: right;
      padding: 0 16px 4px;
      font-size: 9px;
      color: #525252;
    }
    .sacas-line strong {
      font-family: 'Courier New', monospace;
      font-size: 11px;
      color: #1a1a1a;
    }

    /* ── FINANCIAL ── */
    .financial-grid {
      display: grid;
      grid-template-columns: 1fr 1fr 1fr;
      border-top: 1px solid #d4d4d4;
    }
    .financial-cell {
      padding: 6px 12px;
      border-right: 1px solid #d4d4d4;
      text-align: center;
    }
    .financial-cell:last-child { border-right: none; }
    .financial-cell .fin-label { font-size: 7px; font-weight: 700; color: #737373; text-transform: uppercase; }
    .financial-cell .fin-value { font-family: 'Courier New', monospace; font-size: 12px; font-weight: 800; }
    .financial-cell.highlight { background: #1B4332; color: #fff; }
    .financial-cell.highlight .fin-label { color: rgba(255,255,255,0.7); }

    /* ── SIGNATURES ── */
    .signatures {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 20px;
      padding: 10px 16px 8px;
    }
    .sig-line {
      border-top: 1px solid #1a1a1a;
      margin-top: 24px;
      padding-top: 2px;
      font-size: 8px;
      color: #525252;
      font-weight: 600;
      text-transform: uppercase;
      text-align: center;
    }

    /* ── FOOTER ── */
    .footer {
      padding: 4px 16px;
      background: #f5f5f5;
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 7px;
      color: #a3a3a3;
    }
    .footer .system { font-weight: 700; color: #1B4332; font-size: 8px; }

    /* ── PRINT ── */
    @media print {
      body { padding: 0; }
      .no-print { display: none !important; }
      .ticket { border: 1px solid #000; }
    }

    .print-btn {
      display: block;
      margin: 8px auto;
      padding: 8px 20px;
      background: #1B4332;
      color: white;
      border: none;
      font-size: 12px;
      font-weight: 700;
      cursor: pointer;
      text-transform: uppercase;
    }
    .obs-text {
      font-size: 9px;
      color: #525252;
      font-style: italic;
    }
  </style>
</head>
<body>
  <button class="print-btn no-print" onclick="window.print()">🖨️ IMPRIMIR TICKET</button>

  <div class="ticket">
    <!-- HEADER -->
    <div class="header">
      <div class="header-left">
        <h1>${escHtml(fazendaNome)}</h1>
        ${fazendaLocal ? `<div class="subtitle">${escHtml(fazendaLocal)}</div>` : ""}
      </div>
      <div class="header-right">
        <div class="logo-text">AgroGestão</div>
      </div>
    </div>

    <!-- TYPE BAR -->
    <div class="type-bar">
      <span>${tipoLabel} Nº ${ticketNum}</span>
      <span class="situacao">Normal</span>
    </div>

    <!-- GENERAL DATA -->
    <div class="section">
      <div class="section-title">Dados da Operação</div>
      <div class="data-grid">
        <div class="data-row">
          <span class="data-label">Emissão</span>
          <span class="data-value">${fmtDate(data.data)}</span>
        </div>
        <div class="data-row">
          <span class="data-label">Placa</span>
          <span class="data-value mono">${escHtml(data.placa_veiculo || "---")}</span>
        </div>
        <div class="data-row full-width">
          <span class="data-label">${data.tipo === "entrada" ? "Fornecedor / Origem" : "Cliente / Destino"}</span>
          <span class="data-value">${escHtml(data.origem_destino || "---")}</span>
        </div>
        <div class="data-row">
          <span class="data-label">Motorista</span>
          <span class="data-value">${escHtml(data.motorista || "---")}</span>
        </div>
        <div class="data-row">
          <span class="data-label">Nota Fiscal</span>
          <span class="data-value mono">${escHtml(data.nota_fiscal || "---")}</span>
        </div>
        <div class="data-row">
          <span class="data-label">Armazém</span>
          <span class="data-value">${escHtml(armazemNome)}</span>
        </div>
        <div class="data-row">
          <span class="data-label">Safra</span>
          <span class="data-value">${escHtml(data.safra_nome || "---")}</span>
        </div>
      </div>
    </div>

    <!-- PRODUCT -->
    <div class="section">
      <div class="section-title">Produto</div>
      <div class="data-grid">
        <div class="data-row">
          <span class="data-label">Produto</span>
          <span class="data-value" style="font-size:14px;">${produtoLabel}</span>
        </div>
        <div class="data-row">
          <span class="data-label">Classificação</span>
          <span class="data-value">${classLabel}</span>
        </div>
      </div>
    </div>

    <!-- WEIGHING -->
    <div class="section">
      <div class="section-title">Pesagem</div>
      <table class="weight-table">
        <thead>
          <tr>
            <th>Descrição</th>
            <th class="right">Peso (KG)</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style="font-family:inherit;font-size:11px;font-weight:600;color:#525252;">Peso Bruto</td>
            <td class="right">${fmt(pesoBruto)}</td>
          </tr>
          <tr>
            <td style="font-family:inherit;font-size:11px;font-weight:600;color:#525252;">Peso Tara</td>
            <td class="right">${fmt(pesoTara)}</td>
          </tr>
          <tr style="background:#f5f5f5;">
            <td style="font-family:inherit;font-size:11px;font-weight:800;color:#1a1a1a;">Peso Verde (Líquido Bruto)</td>
            <td class="right" style="font-size:16px;color:#1B4332;">${fmt(pesoVerde)}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- CLASSIFICATION & DISCOUNTS -->
    <div class="section">
      <div class="section-title">Classificação & Descontos</div>
      <table class="class-table">
        <thead>
          <tr>
            <th>Fator</th>
            <th>Análise (%)</th>
            <th>Desconto (KG)</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>Umidade</td>
            <td>${fmtPct(umidade)} %</td>
            <td class="discount">${descontoUmidade > 0 ? "-" + fmt(descontoUmidade) : "0"}</td>
          </tr>
          <tr>
            <td>Impureza</td>
            <td>${fmtPct(impureza)} %</td>
            <td class="discount">${descontoImpureza > 0 ? "-" + fmt(descontoImpureza) : "0"}</td>
          </tr>
          <tr>
            <td>Avariados</td>
            <td>${fmtPct(avariados)} %</td>
            <td class="discount">${descontoAvariados > 0 ? "-" + fmt(descontoAvariados) : "0"}</td>
          </tr>
          <tr>
            <td>Quebrados / Partidos</td>
            <td>${fmtPct(quebrados)} %</td>
            <td class="discount">${descontoQuebrados > 0 ? "-" + fmt(descontoQuebrados) : "0"}</td>
          </tr>
          <tr>
            <td>Ardidos</td>
            <td>${fmtPct(ardidos)} %</td>
            <td class="discount">${descontoArdidos > 0 ? "-" + fmt(descontoArdidos) : "0"}</td>
          </tr>
          <tr>
            <td>Esverdeados</td>
            <td>${fmtPct(esverdeados)} %</td>
            <td class="discount">${descontoEsverdeados > 0 ? "-" + fmt(descontoEsverdeados) : "0"}</td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- NET WEIGHT -->
    <div class="net-weight-box">
      <span class="label">Peso Líquido Final</span>
      <span>
        <span class="value">${fmt(pesoLiquido)}</span>
        <span class="unit">KG</span>
      </span>
    </div>
    <div class="sacas-line">
      Equivalente: <strong>${fmt(sacas, 2)} sacas</strong> (60 kg)
    </div>

    <!-- FINANCIAL -->
    <div class="section" style="padding:0; border-bottom:1px solid #d4d4d4;">
      <div style="padding:8px 24px 4px;">
        <div class="section-title">Valores</div>
      </div>
      <div class="financial-grid">
        <div class="financial-cell">
          <div class="fin-label">Preço / Saca</div>
          <div class="fin-value">${fmtCurrency(precoSaca)}</div>
        </div>
        <div class="financial-cell">
          <div class="fin-label">Sacas</div>
          <div class="fin-value">${fmt(sacas, 2)}</div>
        </div>
        <div class="financial-cell highlight">
          <div class="fin-label">Valor Total</div>
          <div class="fin-value">${fmtCurrency(valorTotal)}</div>
        </div>
      </div>
    </div>

    ${data.observacoes
      ? `<div class="section">
      <div class="section-title">Observações</div>
      <div class="obs-text">${escHtml(data.observacoes)}</div>
    </div>`
      : ""
    }

    <!-- SIGNATURES -->
    <div class="signatures">
      <div class="sig-block">
        <div class="sig-line">Assinatura Balanceiro</div>
      </div>
      <div class="sig-block">
        <div class="sig-line">Assinatura Motorista / Transportador</div>
      </div>
    </div>

    <!-- FOOTER -->
    <div class="footer">
      <span>Impresso em: ${now.toLocaleDateString("pt-BR")} às ${now.toLocaleTimeString("pt-BR")}</span>
      <span class="system">AGROGESTÃO</span>
    </div>
  </div>

  <button class="print-btn no-print" onclick="window.print()">🖨️ IMPRIMIR TICKET</button>
</body>
</html>`;

  const printWindow = window.open("", "_blank");
  if (printWindow) {
    printWindow.document.write(html);
    printWindow.document.close();
    setTimeout(() => {
      printWindow.focus();
      printWindow.print();
    }, 400);
  }
}

function escHtml(str) {
  if (!str) return "";
  return String(str)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}
